import React, { Fragment, useEffect, useState } from 'react';
import { Container } from '@/components/container';
import {
    Toolbar,
    ToolbarActions,
    ToolbarDescription,
    ToolbarHeading,
    ToolbarPageTitle,
} from '@/partials/toolbar';
import { PageNavbar } from '@/pages/account';
import { CandidatesContent } from './CandidatesContent';
import { CandidateModal } from '@/pages/buddy/candidate/blocks';
import { KeenIcon } from '@/components';
import axios from 'axios';
import { CandidateDetailsData } from '@/pages/buddy/types/CandidateDetailsData';
import { enqueueSnackbar } from "notistack";
import { useStatistic } from "@/contexts/StatisticContext";
import { NoCompaniesContent } from "@/pages/buddy/candidates/NoCompaniesContent";
import { NoJobsContent } from "@/pages/buddy/candidates/NoJobsContent";
import { NoCandidatesContent } from "@/pages/buddy/candidates/NoCandidatesContent";

const CandidatesPage = () => {
    const [candidates, setCandidates] = useState<CandidateDetailsData[]>([]);
    const [newCandidateModalOpen, setNewCandidateModalOpen] = useState(false);
    const [loadingCandidates, setLoadingCandidates] = useState(true);
    const { statistic, refreshStatistic } = useStatistic();

    const handleOpen = () => setNewCandidateModalOpen(true);
    const handleClose = () => setNewCandidateModalOpen(false);

    const API_URL = import.meta.env.VITE_APP_API_URL;
    const API_CANDIDATES = `${API_URL}/candidates`;

    const fetchCandidateData = async () => {
        try {
            const response = await axios.get<CandidateDetailsData[]>(API_CANDIDATES, {
                headers: { Accept: 'application/json' },
            });

            setCandidates(response.data);

            // Auto-open modal if no candidates are returned
            if (response.data.length === 0) {
                handleOpen();
            }
        } catch (err) {
            enqueueSnackbar(`Failed to fetch candidates data: ${err}`, {
                variant: 'error',
            });
        } finally {
            setLoadingCandidates(false);
        }
    };

    useEffect(() => {
        // Refresh statistics if not already loaded
        if (!statistic) {
            refreshStatistic();
        }
    }, [statistic, refreshStatistic]);

    useEffect(() => {
        // Once statistics are loaded and conditions are met, fetch candidates
        // We only fetch candidates if a company and jobs exist.
        if (statistic && statistic.company_count > 0 && statistic.job_count > 0) {
            fetchCandidateData();
        }
    }, [statistic]);

    // While statistics are loading
    if (!statistic) {
        return (
            <Container>
                <div className="flex flex-col items-center justify-center h-full">
                    <p>Loading statistics...</p>
                </div>
            </Container>
        );
    }

    // If no company exists, prompt user to create one
    if (statistic.company_count === 0) {
        return (
            <Container>
                <NoCompaniesContent />
            </Container>
        );
    }

    // If companies exist but no jobs exist
    if (statistic.job_count === 0) {
        return (
            <Container>
                <NoJobsContent />
            </Container>
        );
    }

    return (
        <Fragment>
            <PageNavbar />
            <CandidateModal mode={'create'} open={newCandidateModalOpen} onClose={handleClose} />

            <Container>
                <Toolbar>
                    <ToolbarHeading>
                        <ToolbarPageTitle />
                        <ToolbarDescription>
                            Efficient team organization with real-time updates
                        </ToolbarDescription>
                    </ToolbarHeading>
                    <ToolbarActions>
                        <button
                            onClick={handleOpen}
                            type="button"
                            className="btn btn-sm btn-primary"
                        >
                            <KeenIcon icon="plus" /> Add New Candidate Profile
                        </button>
                    </ToolbarActions>
                </Toolbar>
            </Container>

            <Container>
                {loadingCandidates ? (
                    <div>Loading candidates...</div>
                ) : candidates.length === 0 ? (
                    <NoCandidatesContent onAddNewCandidate={handleOpen} />
                ) : (
                    <CandidatesContent
                        candidates={candidates}
                        loading={loadingCandidates}
                    />
                )}
            </Container>
        </Fragment>
    );
};

export { CandidatesPage };
